import React, { useEffect, startTransition } from "react";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import Cookies from "js-cookie";
import closeIcon from "@qlik/qlik/src/assets/icons/close.svg";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "../Cta";
import * as styles from "./styles.module.css";

/**
 * NotificationBar component
 * This component is used to display a notification bar with a message, icon and a CTA button.
 * @param {Object} data - The data for the notification bar.
 */
const NotificationBar = ({ data, isSecondaryNotification = false }) => {
    const { icon, cta = [], updatedAt, cookieValidity, message } = data ?? {};

    // Use the AppContext to get and set the notificationExists state
    const {
        notificationExists,
        setNotificationExists,
        secondaryNotificationExists,
        setSecondaryNotificationExists,
    } = useAppContext();
    // Define the cookie name
    const cookieName = "_notification";
    // Get the notification icon if it exists
    const notificationIcon = icon?.length ? icon[0] : null;
    // Get the CTA button if it exists
    const button = typeof cta === "object" ? cta : null;

    // Use the useEffect hook to check if the notification exists
    useEffect(() => {
        const cookieValue = Cookies.get(cookieName);
        if (cookieValue !== updatedAt) {
            startTransition(() => {
                setNotificationExists(true);
                if (isSecondaryNotification) {
                    setSecondaryNotificationExists(true);
                }
            });
        }
    }, [updatedAt, setNotificationExists, setSecondaryNotificationExists]);

    // Handle the button click event
    const handleButtonClick = () => {
        Cookies.set(cookieName, updatedAt, { expires: cookieValidity / 24 });
        setNotificationExists(false);
        if (isSecondaryNotification) {
            setSecondaryNotificationExists(false);
        }
    };
    const wrapperClassNames = `relative container xl:max-w-360 ${
        isSecondaryNotification ? "m-auto" : "px-5 md:px-3.75"
    }`;

    // Determine which notification state to use based on isSecondaryNotification
    const notificationState = isSecondaryNotification ? secondaryNotificationExists : notificationExists;

    // Render the notification bar
    return (
        notificationState &&
        message?.raw && (
            <div
                data-notification
                className="bg-blue py-3.75 relative w-full notification-bar ease-in-out duration-300"
            >
                <div className={`${wrapperClassNames}`}>
                    <div className="row">
                        <div className="col-12">
                            <div className="flex justify-center">
                                <div
                                    className={`flex gap-5 text-white ${
                                        isSecondaryNotification ? "pl-5 pr-5 lg:pl-10 lg:pr-20" : "lg:max-w-184.5"
                                    }`}
                                >
                                    {notificationIcon && (
                                        <div className="w-7.5 h-7.5">
                                            <Media
                                                link={notificationIcon.secure_url}
                                                className="object-contain max-w-min w-7.5 h-7.5"
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`flex flex-col gap-x-15 lg:flex-row ${!isSecondaryNotification &&
                                            "pr-14 lg:pr-0"}`}
                                    >
                                        {message && (
                                            <div
                                                className={`pr-7.5 lg:pr-0 ${styles.title} ${
                                                    isSecondaryNotification ? styles.secondaryTitle : "pr-0"
                                                } mb-5 lg:mb-0 shrink`}
                                            >
                                                {renderRichText(message, formatContentfulRichText())}
                                            </div>
                                        )}
                                        {button && (
                                            <div className="shrink-0 leading-none mt-2 lg:mt-0">
                                                <Cta
                                                    data={{ ...button, type: null }}
                                                    rel="noreferrer"
                                                    className={`lg:mt-2 common-link after:arrow-white text-white`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className={`absolute hover:cursor-pointer ${
                                            isSecondaryNotification
                                                ? "right-5 top-0 lg:right-10 lg:top-2.25"
                                                : " right-5 top-6.25 md:right-0 xl:right-[35px] md:top-1.5"
                                        }`}
                                        onClick={handleButtonClick}
                                    >
                                        {closeIcon && (
                                            <Media
                                                link={closeIcon}
                                                className={`brightness-0 invert ${
                                                    isSecondaryNotification
                                                        ? " w-3.5 h-3.5"
                                                        : " w-3.5 h-3.5 md:w-4.25 md:h-4.25"
                                                }`}
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};
export default NotificationBar;
